import styles from './split.module.css';

export interface Proof {
  id: string;
  rating: number;
  source: 'principal' | 'trustee' | 'guardian' | 'teacher' | 'support';
  quote: string;
  initials: string;
  name: string;
  role: string;
}

const SOURCE_LABEL: Record<Proof['source'], string> = {
  principal: 'principal',
  trustee: 'trustee',
  guardian: 'guardian',
  teacher: 'teacher',
  support: 'internal',
};

/**
 * Testimonials scrolling edge to edge, forever. Pure CSS animation (see
 * split.module.css) — hover to pause; `prefers-reduced-motion` stops it.
 * The list is rendered twice; the track slides one copy's width so the loop
 * is seamless.
 */
export function TestimonialMarquee({ items }: { items: readonly Proof[] }) {
  return (
    <section className={styles.marquee} aria-label="What people say">
      <div className={styles.marqueeTrack}>
        <ProofRow items={items} />
        <ProofRow items={items} duplicate />
      </div>
    </section>
  );
}

function ProofRow({ items, duplicate = false }: { items: readonly Proof[]; duplicate?: boolean }) {
  return (
    <ul className={styles.marqueeRow} aria-hidden={duplicate || undefined}>
      {items.map((item) => (
        <li key={item.id} className={styles.marqueeCard}>
          <div className={styles.marqueeHead}>
            <Stars rating={item.rating} />
            <span className={styles.sourceChip}>{SOURCE_LABEL[item.source]}</span>
          </div>
          <blockquote className={styles.marqueeQuote}>"{item.quote}"</blockquote>
          <div className={styles.marqueeWho}>
            <span className={styles.marqueeAvatar} aria-hidden="true">{item.initials}</span>
            <span>
              <span className={styles.marqueeName}>{item.name}</span>
              <span className={styles.marqueeRole}>{item.role}</span>
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Stars({ rating }: { rating: number }) {
  return (
    <span className={styles.stars} role="img" aria-label={`${rating} out of 5`}>
      {Array.from({ length: 5 }, (_, i) => (
        <svg key={i} width="11" height="11" viewBox="0 0 20 20" fill={i < rating ? 'currentColor' : 'rgba(239,240,231,0.18)'} aria-hidden="true">
          <path d="M10 1.6l2.5 5.4 5.9.7-4.4 4 1.2 5.8L10 14.6 4.8 17.5 6 11.7 1.6 7.7l5.9-.7z" />
        </svg>
      ))}
    </span>
  );
}
